import { forwardRef, useEffect, useImperativeHandle, useRef } from "react";
import {
  FranklinProvider,
  useFranklinContext,
} from "../context/franklin-context";
import type {
  FranklinHelpCenterHandle,
  FranklinHelpCenterProps,
} from "../types";
import { Sidebar } from "./Sidebar/sidebar";

const DEFAULT_SIDEBAR_WIDTH = "420px";

const FranklinHelpCenterInner = forwardRef<FranklinHelpCenterHandle>(
  (_, ref) => {
    const context = useFranklinContext();
    const { isOpen } = context;

    useImperativeHandle(ref, () => ({
      open: context.open,
      close: context.close,
      toggle: context.toggle,
      pushContext: context.pushContext,
      setTab: context.setActiveTab,
    }));

    // Dispatch events for body class management
    useEffect(() => {
      if (isOpen) {
        document.body.classList.add("franklin-sidebar-open");
      } else {
        document.body.classList.remove("franklin-sidebar-open");
      }
    }, [isOpen]);

    return <Sidebar />;
  }
);

FranklinHelpCenterInner.displayName = "FranklinHelpCenterInner";

export const FranklinHelpCenter = forwardRef<
  FranklinHelpCenterHandle,
  FranklinHelpCenterProps
>(
  (
    {
      orgId,
      sdkKey,
      shiftTargetSelector,
      defaultTab = "ai",
      theme,
      onOpen,
      onClose,
      apiEndpoint = "/api/franklin",
    },
    ref
  ) => {
    const targetRef = useRef<HTMLElement | null>(null);
    const previousWidthRef = useRef<string | null>(null);

    useEffect(() => {
      if (typeof document === "undefined") {
        return;
      }

      let element: HTMLElement | null = document.body;
      if (shiftTargetSelector) {
        const queried = document.querySelector(
          shiftTargetSelector
        ) as HTMLElement | null;
        if (queried) {
          element = queried;
        }
      }

      targetRef.current = element;

      if (element) {
        element.setAttribute("data-franklin-shift", "");
      }

      return () => {
        element?.removeAttribute("data-franklin-shift");
      };
    }, [shiftTargetSelector]);

    useEffect(() => {
      if (typeof document === "undefined") {
        return;
      }

      const root = document.documentElement;
      const current = root.style.getPropertyValue("--franklin-width");

      if (current) {
        previousWidthRef.current = current;
      } else {
        previousWidthRef.current = null;
        root.style.setProperty("--franklin-width", DEFAULT_SIDEBAR_WIDTH);
      }

      return () => {
        if (previousWidthRef.current === null) {
          root.style.removeProperty("--franklin-width");
        } else if (previousWidthRef.current) {
          root.style.setProperty("--franklin-width", previousWidthRef.current);
        }
      };
    }, []);

    // Apply theme CSS variables
    useEffect(() => {
      if (typeof document === "undefined" || !theme) {
        return;
      }

      const root = document.documentElement;
      const previousValues = new Map<string, string>();
      let previousThemeAttr: string | null = null;

      const setVariable = (token: string, value?: string | number) => {
        if (value === undefined) {
          return;
        }
        const formatted =
          typeof value === "number" ? `${value}px` : value.toString();
        previousValues.set(token, root.style.getPropertyValue(token));
        root.style.setProperty(token, formatted);
      };

      setVariable("--franklin-accent", theme.accent);
      setVariable("--franklin-surface", theme.surface);
      setVariable("--franklin-border", theme.border);
      setVariable("--franklin-radius", theme.radius);
      setVariable("--franklin-font", theme.fontFamily);

      if (typeof document !== "undefined") {
        previousThemeAttr = document.body.getAttribute("data-franklin-theme");

        if (theme.mode && theme.mode !== "auto") {
          document.body.setAttribute("data-franklin-theme", theme.mode);
        } else if (theme.mode === "auto") {
          document.body.removeAttribute("data-franklin-theme");
        }
      }

      return () => {
        previousValues.forEach((value, token) => {
          if (value) {
            root.style.setProperty(token, value);
          } else {
            root.style.removeProperty(token);
          }
        });

        if (typeof document !== "undefined") {
          if (previousThemeAttr) {
            document.body.setAttribute(
              "data-franklin-theme",
              previousThemeAttr
            );
          } else {
            document.body.removeAttribute("data-franklin-theme");
          }
        }
      };
    }, [theme]);

    useEffect(() => {
      const handleShift = (event: Event) => {
        const { detail } = event as CustomEvent<{ isOpen?: boolean }>;
        const target = targetRef.current;
        if (!target) {
          return;
        }

        const computedWidth =
          typeof document !== "undefined"
            ? getComputedStyle(document.documentElement)
                .getPropertyValue("--franklin-width")
                .trim()
            : "";
        const sidebarWidth = computedWidth || DEFAULT_SIDEBAR_WIDTH;

        target.style.transition =
          "margin-right 0.4s cubic-bezier(0.4, 0, 0.2, 1)";
        if (detail?.isOpen) {
          target.style.marginRight = sidebarWidth;
        } else {
          target.style.marginRight = "";
        }
      };

      window.addEventListener("franklin:state-change", handleShift);

      return () => {
        window.removeEventListener("franklin:state-change", handleShift);
        if (targetRef.current) {
          targetRef.current.style.marginRight = "";
          targetRef.current.style.transition = "";
        }
      };
    }, [shiftTargetSelector]);

    return (
      <FranklinProvider
        apiEndpoint={apiEndpoint}
        defaultTab={defaultTab}
        onClose={() => {
          window.dispatchEvent(
            new CustomEvent("franklin:state-change", {
              detail: {
                isOpen: false,
                target: shiftTargetSelector ?? "body",
              },
            })
          );
          onClose?.();
        }}
        onOpen={() => {
          window.dispatchEvent(
            new CustomEvent("franklin:state-change", {
              detail: {
                isOpen: true,
                target: shiftTargetSelector ?? "body",
              },
            })
          );
          onOpen?.();
        }}
        orgId={orgId}
        sdkKey={sdkKey}
        theme={theme}
      >
        <FranklinHelpCenterInner ref={ref} />
      </FranklinProvider>
    );
  }
);

FranklinHelpCenter.displayName = "FranklinHelpCenter";
